// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Search from "@insite/mobius/Icons/Search";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@steiner/App/Components/Common/XngZone";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => ({
    loaded: !!getProductListDataView(state).value,
    productFilters: state.pages.productList.productFilters,
    hideSearchWithin: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListSearchWithinStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    searchTextField?: TextFieldPresentationProps;
    // XNG-Change
    facetsSectionWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
}

export const searchWithinStyles: ProductListSearchWithinStyles = {
    titleText: {
        variant: "h6",
    },
    searchTextField: {
        iconProps: { src: Search },
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
};

const styles = searchWithinStyles;

const ProductListSearchWithin: FC<Props> = ({
    id,
    loaded,
    addProductFilters,
    productFilters,
    hideSearchWithin,
    // XNG-Changes
    isTabletScreen,
}) => {
    if (!loaded) {
        return null;
    }

    const [searchText, setSearchText] = useState("");

    const doSearch = () => {
        if (searchText.trim() !== "") {
            const uniqueSearchWithinQueries = searchText
                .split(" ")
                .filter((value, index, array) => array.indexOf(value) === index);

            addProductFilters({ searchWithinQueries: uniqueSearchWithinQueries });
        }
        setSearchText("");
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            doSearch();
        } else {
            setSearchText(event.currentTarget.value);
        }
    };

    let pageType: string;
    if (productFilters.pageProductLineId) {
        pageType = "Product Line";
    } else if (productFilters.pageBrandId) {
        pageType = "Brand";
    } else if (productFilters.pageCategoryId) {
        pageType = "Category";
    } else {
        pageType = "Results";
    }

    const label = translate(`Search Within ${pageType}`);

    if (hideSearchWithin) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            {!isTabletScreen &&
                createWidgetElement("ProductList/ProductListFilterList", { fields: { viewType: "title" } })}
            {/* XNG-Change Add wrapper */}
            <StyledWrapper {...styles.searchWrapper}>
                <Typography forwardAs="label" htmlFor="productsKeywordSearch" {...styles.titleText}>
                    {label}
                </Typography>
                <TextField
                    {...styles.searchTextField}
                    // XNG-Changes: update iconClickableProps
                    iconClickableProps={{
                        onClick: doSearch,
                        className: "searchClickable",
                        "aria-label": `Search within ${pageType}`,
                        "aria-roledescription": `Search within ${pageType}`,
                    }}
                    value={searchText}
                    onChange={event => setSearchText(event.currentTarget.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={translate("Search by Keyword")}
                    type="text"
                    data-test-selector="productListSearchWithin"
                    id="productsKeywordSearch"
                />
                <XngZone contentId={id} zoneName="Content01" hint="zone for ProductListFilterList" />
            </StyledWrapper>
            <StyledWrapper {...styles.facetsSectionWrapper}>
                {/* XNG-Changes  */}
                <XngZone contentId={id} zoneName="Content03" hint="zone for Stocked Filter" />
                <XngZone contentId={id} zoneName="Content02" hint="zone for facets" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListSearchWithin),
    definition: {
        group: "Product List",
        displayName: "Search Within",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
